import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LeadmeLogo from '../assets/images/leadme-site-logo.png';

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { refreshUserData } = useAuth();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');

    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotMessage('Om e-postadressen finns i systemet har ett nytt lösenord skickats.');
    } catch (error) {
      setForgotMessage('Ett fel uppstod. Försök igen senare.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      const data = response.data;
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('role', data.role.toLowerCase());
      localStorage.setItem('userId', data.userId);
      
      if (data.requirePasswordChange) {
        localStorage.setItem('requirePasswordChange', 'true');
      }

      await refreshUserData();
      
      navigate(data.landingPage);
      
    } catch (error) {
      console.error('Detailed login error:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        setErrorMessage(error.response.data.message || 'Inloggningen misslyckades. Kontrollera dina uppgifter.');
      } else if (error.request) {
        console.error('Error request:', error.request);
        setErrorMessage('Kunde inte nå servern. Kontrollera din internetanslutning.');
      } else {
        setErrorMessage('Ett fel uppstod. Försök igen senare.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-leadme-pink-light/20">
      <div className="w-full max-w-md">
        <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl p-10 space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img 
              src={LeadmeLogo} 
              alt="LeadMe Logotyp" 
              className="h-8 transition-transform duration-200" 
            />
          </div>
          <p>
            Var vänlig och logga in med dina uppgifter eller <Link 
                to="/register" 
                className="font-semibold text-primary"
              >
                Registrera dig
              </Link>
          </p>

          {showForgotPassword ? (
            <div className="space-y-6">
              {forgotMessage ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                    <p className="text-sm text-green-700 text-center">{forgotMessage}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setForgotMessage(''); setForgotEmail(''); }}
                    className="w-full text-sm text-primary font-semibold hover:underline"
                  >
                    Tillbaka till inloggning
                  </button>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleForgotPassword}>
                  <p className="text-sm text-gray-600">
                    Ange din e-postadress så skickar vi ett tillfälligt lösenord.
                  </p>
                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-post
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      required
                      disabled={forgotLoading}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                      placeholder="din@email.se"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full py-3 px-4 bg-leadme-pink text-white text-lg font-semibold rounded-xl
                    transition-all duration-200
                    hover:bg-leadme-pink-dark active:bg-leadme-pink-dark
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
                  >
                    {forgotLoading ? (
                      <svg className="animate-spin h-6 w-6 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : 'Skicka'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }}
                    className="w-full text-sm text-primary font-semibold hover:underline"
                  >
                    Tillbaka till inloggning
                  </button>
                </form>
              )}
            </div>
          ) : (
            <>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-post
                  </label>
                  <input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    required
                    disabled={loading}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                    placeholder="din@email.se"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Lösenord
                  </label>
                  <input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    required
                    disabled={loading}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 backdrop-blur-sm focus:border-primary focus:ring-0"
                    placeholder="••••••••"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setErrorMessage(''); }}
                    className="text-sm text-primary font-semibold hover:underline"
                  >
                    Glömt lösenord?
                  </button>
                </div>

                <button
                  type="submit"
                  data-testid="login-button"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-leadme-pink text-white text-lg font-semibold rounded-xl
                  transition-all duration-200
                  hover:bg-leadme-pink-dark active:bg-leadme-pink-dark
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
                >
                  {loading ? (
                    <svg className="animate-spin h-6 w-6 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : 'Logga in'}
                </button>
              </form>

              {errorMessage && (
                <div className="p-4 rounded-xl bg-leadme-red-light/20 border border-leadme-red-light">
                  <p
                    data-testid="error-message"
                    className="text-sm text-leadme-red text-center"
                  >
                    {errorMessage}
                  </p>
                </div>
              )}
            </>
          )}

          <div>
            <p className="text-center text-sm text-gray-600">
              Har du inget konto?{' '}
              <Link 
                to="/register" 
                className="font-semibold text-primary"
              >
                Registrera dig här →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;